import { createPortal } from 'react-dom';
import {
  Checkbox,
  Box,
  Divider,
  IconButton,
  Slide,
  Slider,
  FormControlLabel,
  CircularProgress,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CloseIcon from '@mui/icons-material/Close';
import OpacityIcon from '@mui/icons-material/Opacity';
import { useContext } from './context';
import { LayerButton } from './LayerButton';
import { Legend } from './Legend';

export function PhoneLayers() {
  const {
    layers,
    isOpen,
    setOpen,
    visibleLegends,
    visibleOpacities,
    toggleLegend,
    toggleOpacity,
    changeOpacity,
    buildLayerName,
    containerRef,
  } = useContext();

  const {
    toggle: toggleLayer,
    isLoading,
    isHidden,
    isDisabled,
    isVisible,
  } = layers;

  return (
    <Box sx={{ m: 1 }}>
      <LayerButton sx={{ width: 'auto', borderRadius: 4, p: 1.5 }} />

      {containerRef?.current &&
        createPortal(
          <Slide direction="left" in={!!isOpen} mountOnEnter unmountOnExit>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1003,
                bgcolor: 'white',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: 1,
                }}
              >
                <Box sx={{ width: 40 }} />
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ flex: 1, fontWeight: 500 }}
                >
                  Layers
                </Typography>
                <IconButton size="small" onClick={() => setOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ flex: 1, overflow: 'auto' }}>{buildLayers()}</Box>
              <Divider />
            </Box>
          </Slide>,
          containerRef.current,
        )}
    </Box>
  );

  function buildLayers() {
    return layers
      .filter((layer) => !isHidden(layer))
      .map((layer) => {
        const layerVisible = isVisible(layer);
        const layerDisabled = isDisabled(layer);
        const layerLoading = isLoading(layer);
        const showLegend =
          layerVisible && visibleLegends.includes(layer.id) && layer.legend;
        const showOpacity =
          layerVisible &&
          visibleOpacities.includes(layer.id) &&
          layer.opacityControl;

        function checkboxColor() {
          if (layer.error) return 'error';
          return layerDisabled ? 'primary' : 'success';
        }

        return (
          <Box key={layer.id}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', px: 1, height: 44 }}
            >
              <FormControlLabel
                disabled={layerDisabled}
                sx={{ flex: 1, m: 0 }}
                control={
                  layerLoading ? (
                    <CircularProgress
                      color="inherit"
                      sx={{ m: 1.25 }}
                      size={22}
                    />
                  ) : (
                    <Checkbox
                      color={checkboxColor()}
                      inputProps={{
                        'data-testid': `layer-${layer.id}-visibility`,
                      }}
                      checked={layerVisible}
                      onChange={() => toggleLayer(layer)}
                    />
                  )
                }
                label={buildLayerName(layer)}
              />
              {layerVisible && !layerDisabled && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {layer.legend && (
                    <IconButton
                      color={showLegend ? 'primary' : 'inherit'}
                      size="small"
                      onClick={() => toggleLegend(layer)}
                    >
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {layer.opacityControl && (
                    <IconButton
                      color={showOpacity ? 'primary' : 'inherit'}
                      size="small"
                      onClick={() => toggleOpacity(layer)}
                    >
                      <OpacityIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>

            {showLegend && (
              <Box sx={{ pl: 5, pr: 2, pb: 1 }}>
                <Typography variant="subtitle3" color="text.secondary">
                  Legend
                </Typography>

                <Box
                  sx={{
                    '& .MuiListItem-root': {
                      pl: 0,
                    },

                    '& .MuiListItemIcon-root svg': {
                      fontSize: 16,
                    },

                    '& .MuiListItemText-primary': {
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontSize: 10,
                      lineHeight: 2,
                    },
                  }}
                >
                  <Legend layer={layer} />
                </Box>
              </Box>
            )}

            {showOpacity && (
              <Box sx={{ pl: 5, pr: 2, pb: 1 }}>
                <Typography variant="subtitle3" color="text.secondary">
                  Opacity
                </Typography>
                <Slider
                  sx={{ width: 220, display: 'block', py: 1 }}
                  color="success"
                  size="small"
                  defaultValue={
                    (layer.dataLayer?.style?.fillOpacity ?? 0.7) * 100
                  }
                  onChange={(event) => changeOpacity(layer, event.target.value)}
                />
              </Box>
            )}

            <Divider />
          </Box>
        );
      });
  }
}
